import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import type { MainMenuItem, MenuState } from '../types';
import { getMatchingRoutes } from '../utils';
import NavigationItem from './NavigationItem';

const NavList = styled.ul<{ $isScrollable: boolean }>`
    list-style-type: none;
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;

    ${({ $isScrollable }) =>
      $isScrollable &&
      css`
      overflow-x: hidden;
      scrollbar-width: thin;
      overflow-y: auto;
    `}
`;

const SCROLLBAR_WIDTH = 14;

type DesktopNavListGroupProps = {
  id: string;
  group: 'top' | 'bottom';
  groupedNavigationItems: MainMenuItem[];
  subNavState: [MenuState | null, Dispatch<SetStateAction<MenuState | null>>];
  extraNavItem?: ReactNode;
};

const DesktopNavListGroup = ({
  id,
  group,
  subNavState,
  groupedNavigationItems,
  extraNavItem,
}: DesktopNavListGroupProps) => {
  const [isSubNavOpen, setIsSubNavOpen] = subNavState;

  // offset for sub-nav items to cover the scrollbar
  const [subNavOffset, setSubNavOffset] = useState(0);
  const [itemHeight, setItemHeight] = useState(0);

  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    /**
     * The position of sub-navigation popup should slightly shift to the left
     * when the top nav group gets browser's scrollbar.(see: CP-402)
     * It detects whether the scrollbar has appeared or not.
     * */
    const checkResize = group === 'top';

    let resizeObserver: ResizeObserver | null = null;

    if (checkResize) {
      resizeObserver = new ResizeObserver(entries => {
        const navListSize = entries[0].contentRect;
        // Check if the total height of nav items are taller than the list frame
        if (
          navListSize.height <
          itemHeight * (groupedNavigationItems.length + (extraNavItem ? 1 : 0))
        ) {
          // with scrollbar
          setSubNavOffset(navListSize.width - SCROLLBAR_WIDTH);
        } else {
          // without scrollbar
          setSubNavOffset(navListSize.width);
        }
      });

      if (listRef.current) {
        resizeObserver.observe(listRef.current);
      }
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [groupedNavigationItems.length, itemHeight, group, extraNavItem]);

  return (
    <NavList id={id} {...(group === 'top' ? { ref: listRef } : {})} $isScrollable={group === 'top'}>
      {extraNavItem}

      {groupedNavigationItems.map((navItem, _i) => (
        <NavigationItem
          key={navItem.menuState}
          topGroup={group === 'top'}
          setItemHeight={setItemHeight}
          itemData={{
            ...navItem,
            matchingRoutes: getMatchingRoutes(navItem.items),
          }}
          isSubNavOpen={isSubNavOpen}
          subNavOffset={subNavOffset}
          open={() => setIsSubNavOpen(navItem.menuState)}
          close={() => setIsSubNavOpen(state => (state === navItem.menuState ? null : state))}
        />
      ))}
    </NavList>
  );
};

export default DesktopNavListGroup;
